/**
 * useServiceWorker.ts
 * Hook pour enregistrer et gérer le Service Worker
 */
import { useEffect, useState } from 'react';
import { logger } from '../utils/ProfessionalLogger';

export function useServiceWorker() {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // Enregistrer le Service Worker
      navigator.serviceWorker
        .register('/sw.js', {
          scope: '/',
        })
        .then(reg => {
          logger.info('✅ Service Worker enregistré:', reg);
          setRegistration(reg);

          // Vérifier les mises à jour
          reg.addEventListener('updatefound', () => {
            const newWorker = reg.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // Nouveau Service Worker disponible
                  setUpdateAvailable(true);
                  logger.info('🔄 Nouveau Service Worker disponible');
                }
              });
            }
          });
        })
        .catch(error => {
          logger.error("❌ Erreur lors de l'enregistrement du Service Worker:", error);
        });

      // Écouter les messages du Service Worker
      navigator.serviceWorker.addEventListener('message', event => {
        if (event.data && event.data.type === 'CACHE_UPDATED') {
          logger.info('📦 Cache mis à jour');
        }
      });

      // Écouter les erreurs du Service Worker
      navigator.serviceWorker.addEventListener('error', error => {
        logger.error('❌ Erreur Service Worker:', error);
      });
    } else {
      logger.warn('⚠️ Service Worker non supporté par ce navigateur');
    }
  }, []);

  /**
   * Mettre à jour le Service Worker
   */
  const updateServiceWorker = async () => {
    if (registration) {
      try {
        await registration.update();
        setUpdateAvailable(false);
        logger.info('✅ Service Worker mis à jour');
      } catch (error) {
        logger.error('❌ Erreur lors de la mise à jour du Service Worker:', error);
      }
    }
  };

  /**
   * Mettre en cache un template
   */
  const cacheTemplate = async (url: string, data: Blob | ArrayBuffer) => {
    if (registration && registration.active) {
      registration.active.postMessage({
        type: 'CACHE_TEMPLATE',
        url,
        data,
      });
    }
  };

  return {
    registration,
    updateAvailable,
    updateServiceWorker,
    cacheTemplate,
  };
}
